/**
 * Import a client list -- from any system, by saying what its columns mean.
 *
 * Rebuilt 2026-09-28 on the pattern of EQ Accounts' take-on import (Wimpie:
 * "set a minimum column requirement and let the user that is importing map
 * the columns from their list to our fields"). Four steps on one page:
 *
 *   1. choose the client list, and (optionally) a separate services list;
 *   2. say what each column is -- every dropdown pre-filled by the server's
 *      guess, only "Client name" required;
 *   3. check: coded values, e-mail domains, services, and every row -- any
 *      change here needs a re-check before importing;
 *   4. import.
 *
 * The server does all the checking (server/src/client-import.ts) and does it
 * AGAIN at import. This page never decides what is valid.
 *
 * The files are read in the browser and sent as text. They are never uploaded
 * as files, never stored by the server, and not kept here after the page
 * closes: they are real client data. UTF-16 exports (SmartPractice's services
 * list is one) are decoded here, from the byte-order mark.
 */

import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  api,
  type AuthState,
  type ImportAnalysis,
  type ImportConcept,
  type ImportPreview,
  type ImportRequest,
  type ImportResult,
  type ObligationTypeInUse,
} from '../api'
import { AppShell } from '../components/AppShell'
import { ErrorState } from '../components/States'
import { vatTerm } from '../lib/vatTerms'

const ACTION_WORDS = {
  create: ['New client', 'bg-eq-success-bg text-eq-success'],
  fill: ['Fills blanks', 'bg-eq-ice text-eq-navy'],
  skip: ['Nothing new', 'bg-eq-neutral-bg text-eq-neutral'],
  error: ['Cannot import', 'bg-eq-danger-bg text-eq-danger'],
} as const

const FIELD_WORDS: Record<string, string> = {
  registeredName: 'legal name', tradingName: 'trading name', entityType: 'entity type',
  registrationNumber: 'registration number', idNumber: 'ID number', incomeTaxNumber: 'income tax number',
  vatNumber: vatTerm('VAT_TERM_VAT_REGISTRATION_NUMBER'), payeNumber: 'PAYE number', uifNumber: 'UIF number', coidaNumber: 'COIDA number',
  financialYearEndMonth: 'year-end', entityTypeSourceHint: 'entity type source',
}

const CONCEPT_TITLES: Record<string, string> = {
  ENTITY_TYPE: 'Entity types', STATUS: 'Client status', VAT_REGISTERED: vatTerm('VAT_TERM_VAT_REGISTRATION_STATUS'),
  VAT_CATEGORY: vatTerm('VAT_TERM_VAT_TAX_PERIOD_FREQUENCY'), YEAR_END: 'Financial year-end', GROUP_CODE: 'Group codes shared by two or more clients',
}

/** Bytes to text: the byte-order mark decides, UTF-8 otherwise. */
async function readText(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer())
  if (bytes[0] === 0xff && bytes[1] === 0xfe) return new TextDecoder('utf-16le').decode(bytes.subarray(2))
  if (bytes[0] === 0xfe && bytes[1] === 0xff) return new TextDecoder('utf-16be').decode(bytes.subarray(2))
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes)
  } catch {
    // Not UTF-8: an older Excel "CSV" is Windows-1252.
    return new TextDecoder('windows-1252').decode(bytes)
  }
}

const H2 = 'mb-2 font-heading text-[11px] font-bold uppercase tracking-[.08em] text-eq-neutral'
const CARD = 'mb-4 rounded-eq-md border border-eq-border bg-white px-5 py-4'

function ConceptSelect({
  concepts,
  value,
  onChange,
  label,
}: {
  concepts: ImportConcept[]
  value: string
  onChange: (v: string) => void
  label: string
}) {
  const groups = useMemo(() => {
    const m = new Map<string, ImportConcept[]>()
    for (const c of concepts) m.set(c.group, [...(m.get(c.group) ?? []), c])
    return [...m]
  }, [concepts])
  return (
    <select
      aria-label={`What column "${label}" is`}
      className={['eq-input-sm w-full min-w-[11rem]', value === 'IGNORE' ? 'text-eq-helper' : 'font-semibold text-eq-navy'].join(' ')}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      {groups.map(([group, list]) => (
        <optgroup key={group} label={group}>
          {list.map((c) => (
            <option key={c.key} value={c.key}>
              {c.label}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  )
}

/** Step 2 for one file: a dropdown on every column, above a few rows of it. */
function MapColumns({
  analysis,
  map,
  setMap,
  title,
}: {
  analysis: ImportAnalysis
  map: string[]
  setMap: (m: string[]) => void
  title: string
}) {
  const single = new Set(analysis.concepts.filter((c) => !c.multiple).map((c) => c.key))
  const counts = new Map<string, number>()
  for (const k of map) counts.set(k, (counts.get(k) ?? 0) + 1)
  const clash = [...counts].filter(([k, n]) => single.has(k) && n > 1).map(([k]) => analysis.concepts.find((c) => c.key === k)!.label)
  const needs = analysis.kind === 'clients'
    ? [['CLIENT_NAME', 'Client name'] as const]
    : [['LINK', 'Client ID or client name'] as const, ['SERVICE', 'At least one service'] as const]
  const met = (k: string) =>
    k === 'LINK' ? map.includes('LINK_ID') || map.includes('LINK_NAME')
      : k === 'SERVICE' ? map.includes('SERVICE') || map.includes('SERVICE_TAGS')
        : map.includes(k)
  return (
    <section className={CARD}>
      <h2 className={H2}>{title}</h2>
      <p className="mb-1 text-[14px]">{analysis.hint}</p>
      <p className="mb-3 text-[12px] text-eq-helper">
        {analysis.rowCount} rows, {analysis.delimiter === 'tab' ? 'tab' : `"${analysis.delimiter}"`}-separated
        {analysis.repeatedHeaderRows > 0 && `; ${analysis.repeatedHeaderRows} repeated heading rows skipped`}.
      </p>
      <ul className="mb-3 flex flex-wrap gap-2 text-[12.5px]">
        {needs.map(([k, label]) => (
          <li key={k} className={['rounded-eq-pill px-2.5 py-0.5 font-semibold', met(k) ? 'bg-eq-success-bg text-eq-success' : 'bg-eq-danger-bg text-eq-danger'].join(' ')}>
            {met(k) ? '✓' : '✗'} {label}
          </li>
        ))}
        {clash.map((c) => (
          <li key={c} className="rounded-eq-pill bg-eq-danger-bg px-2.5 py-0.5 font-semibold text-eq-danger">
            “{c}” is chosen for two columns
          </li>
        ))}
      </ul>
      <div className="overflow-x-auto">
        <table className="border-collapse text-[12.5px]">
          <thead>
            <tr>
              {analysis.headers.map((h, i) => (
                <th key={i} className="border-b border-eq-border px-2 pb-2 text-left align-bottom">
                  <div className="mb-1 font-heading text-[11.5px] font-bold text-eq-neutral">{h || `(column ${i + 1})`}</div>
                  <ConceptSelect
                    concepts={analysis.concepts}
                    value={map[i] ?? 'IGNORE'}
                    label={h}
                    onChange={(v) => setMap(map.map((x, j) => (j === i ? v : x)))}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {analysis.sample.slice(0, 3).map((r, ri) => (
              <tr key={ri}>
                {analysis.headers.map((_, i) => (
                  <td key={i} className={['max-w-[14rem] truncate border-b border-eq-border px-2 py-1', map[i] === 'IGNORE' ? 'text-eq-helper' : ''].join(' ')}>
                    {r[i]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

export function ImportClientsPage({
  obligationTypes,
  auth,
}: {
  obligationTypes: ObligationTypeInUse[]
  auth: AuthState
}) {
  const [clientFile, setClientFile] = useState<{ name: string; text: string; analysis: ImportAnalysis } | null>(null)
  const [servicesFile, setServicesFile] = useState<{ name: string; text: string; analysis: ImportAnalysis } | null>(null)
  const [clientMap, setClientMapRaw] = useState<string[]>([])
  const [servicesMap, setServicesMapRaw] = useState<string[]>([])
  const [valueMap, setValueMapRaw] = useState<Record<string, Record<string, string>>>({})
  const [serviceMap, setServiceMapRaw] = useState<Record<string, string>>({})
  const [dropDomains, setDropDomainsRaw] = useState<string[] | undefined>(undefined)
  const [suggest, setSuggestRaw] = useState(true)
  const [from, setFromRaw] = useState<string | undefined>(undefined)

  // A new id per file chosen: the same file confirmed twice is one import.
  const [importId, setImportId] = useState<string>(() => crypto.randomUUID())
  const [preview, setPreview] = useState<ImportPreview | null>(null)
  const [stale, setStale] = useState(false)
  const [excluded, setExcluded] = useState<Set<number>>(new Set())
  const [result, setResult] = useState<ImportResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // Any change after a check makes the check out of date.
  const touch = <T,>(set: (v: T) => void) => (v: T) => { set(v); setStale(true) }
  const setClientMap = touch(setClientMapRaw)
  const setServicesMap = touch(setServicesMapRaw)
  const setValueMap = touch(setValueMapRaw)
  const setServiceMap = touch(setServiceMapRaw)
  const setDropDomains = touch(setDropDomainsRaw)
  const setSuggest = touch(setSuggestRaw)
  const setFrom = touch(setFromRaw)

  const reset = () => {
    setPreview(null)
    setResult(null)
    setExcluded(new Set())
    setValueMapRaw({})
    setServiceMapRaw({})
    setDropDomainsRaw(undefined)
    setStale(false)
    setImportId(crypto.randomUUID())
  }

  const choose = async (file: File | undefined, kind: 'clients' | 'services') => {
    setError(null)
    reset()
    if (file === undefined) return
    if (/\.(xlsx|xls|xlsm)$/i.test(file.name)) {
      setError('That is an Excel workbook. Open it in Excel and use File > Save As > CSV, then choose the CSV here. (Or, better, export straight to CSV from the old system: Excel rewrites long numbers such as ID numbers in scientific notation and their digits are lost.)')
      return
    }
    setBusy(true)
    try {
      const text = await readText(file)
      const analysis = await api.importAnalyse(text, kind)
      if (kind === 'clients') {
        setClientFile({ name: file.name, text, analysis })
        setClientMapRaw(analysis.columnMap)
      } else {
        setServicesFile({ name: file.name, text, analysis })
        setServicesMapRaw(analysis.columnMap)
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const request = (): ImportRequest | null =>
    clientFile === null ? null : {
      text: clientFile.text,
      columnMap: clientMap,
      valueMap,
      options: { dropEmailDomains: dropDomains, suggestEntityTypes: suggest, obligationsFrom: from },
      services: servicesFile === null ? null : { text: servicesFile.text, columnMap: servicesMap, serviceMap },
    }

  const check = async () => {
    const req = request()
    if (req === null || busy) return
    setBusy(true)
    setError(null)
    try {
      const p = await api.importPreview(req)
      setPreview(p)
      setStale(false)
      // Rows that cannot be imported start out excluded, so the button is
      // usable -- and the person can see exactly which ones those are.
      setExcluded(new Set(p.rows.filter((r) => r.action === 'error').map((r) => r.row)))
      if (dropDomains === undefined) setDropDomainsRaw(p.emailDomains.filter((d) => d.dropped).map((d) => d.domain))
      if (from === undefined) setFromRaw(p.obligationsFrom)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const willWrite = useMemo(
    () => (preview === null ? 0 : preview.rows.filter((r) => (r.action === 'create' || r.action === 'fill' || r.work.length > 0) && !excluded.has(r.row)).length),
    [preview, excluded]
  )
  const blocked = preview !== null && (preview.unmappedValues > 0 || (preview.services?.unmappedServices ?? 0) > 0)

  const confirm = async () => {
    const req = request()
    if (req === null || busy || stale || blocked) return
    setBusy(true)
    setError(null)
    try {
      setResult(await api.importCommit(req, importId, [...excluded]))
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const toggle = (row: number) =>
    setExcluded((s) => {
      const next = new Set(s)
      if (next.has(row)) next.delete(row)
      else next.add(row)
      return next
    })

  const setValue = (concept: string, raw: string, target: string) =>
    setValueMap({ ...valueMap, [concept]: { ...(valueMap[concept] ?? {}), [raw]: target } })

  return (
    <AppShell
      obligationTypes={obligationTypes}
      auth={auth}
      eyebrow="Clients"
      title="Import clients"
      subtitle="Bring a client list in from any system. Say what its columns are; nothing is written until you confirm, and existing clients are only ever filled in, never overwritten."
      action={
        preview !== null && result === null ? (
          <button
            type="button"
            onClick={() => void confirm()}
            disabled={busy || stale || blocked || willWrite === 0}
            title={stale ? 'Something changed since the last check -- check again first' : blocked ? 'Map every value and service first' : undefined}
            className={[
              'rounded-eq-sm px-4 py-2 font-heading text-[13px] font-bold',
              busy || stale || blocked || willWrite === 0 ? 'cursor-not-allowed bg-eq-grey text-eq-helper' : 'bg-eq-navy text-white',
            ].join(' ')}
          >
            {busy ? 'Working…' : `Import ${willWrite} client${willWrite === 1 ? '' : 's'}`}
          </button>
        ) : undefined
      }
    >
      <p className="mb-4 text-[12.5px]">
        <Link to="/clients" className="text-eq-navy underline">← All clients</Link>
      </p>

      <section className={CARD}>
        <h2 className={H2}>1. Choose the files</h2>
        <p className="mb-3 text-[14px]">
          Any client list saved as CSV — from SmartPractice, another system, or the{' '}
          <a href={api.importTemplateUrl} className="font-semibold text-eq-navy underline">Equilibrium template</a>.
          Only the client’s name is required. Services can be columns in the same list, or a second file.
        </p>
        <div className="flex flex-wrap gap-6">
          {([['clients', 'Client list', clientFile], ['services', 'Services list (optional)', servicesFile]] as const).map(([kind, label, f]) => (
            <label key={kind} className="inline-flex cursor-pointer items-center gap-3">
              <span className="rounded-eq-sm border border-eq-navy px-3 py-1.5 font-heading text-[12.5px] font-bold text-eq-navy">{label}…</span>
              <input
                type="file"
                accept=".csv,.tsv,.txt,text/csv,text/tab-separated-values"
                className="sr-only"
                disabled={kind === 'services' && clientFile === null}
                onChange={(e) => void choose(e.target.files?.[0], kind)}
              />
              <span className="text-[13px] text-eq-neutral">{f?.name ?? 'No file chosen'}</span>
              {kind === 'services' && f !== null && (
                <button type="button" className="text-[12px] underline" onClick={() => { setServicesFile(null); setStale(true) }}>
                  remove
                </button>
              )}
            </label>
          ))}
        </div>
        <p className="mt-2 text-[12px] text-eq-helper">The files are checked here and then discarded. They are not stored.</p>
      </section>

      {error !== null && <div className="mb-4"><ErrorState message={error} /></div>}
      {busy && preview === null && <p className="mb-4 text-[12.5px] text-eq-helper">Reading…</p>}

      {clientFile !== null && result === null && (
        <MapColumns analysis={clientFile.analysis} map={clientMap} setMap={setClientMap} title="2. Say what each column of the client list is" />
      )}
      {servicesFile !== null && result === null && (
        <MapColumns analysis={servicesFile.analysis} map={servicesMap} setMap={setServicesMap} title="2b. …and each column of the services list" />
      )}

      {clientFile !== null && result === null && (
        <div className="mb-4 flex items-center gap-3">
          <button type="button" className="eq-btn-primary" disabled={busy} onClick={() => void check()}>
            {preview === null ? 'Check the import' : 'Check again'}
          </button>
          {stale && preview !== null && (
            <span className="text-[12.5px] font-semibold text-eq-warning">Something changed since the last check. Check again before importing.</span>
          )}
        </div>
      )}

      {result !== null && <Done result={result} />}

      {preview !== null && result === null && (
        <>
          {Object.keys(preview.values).length > 0 && (
            <section className={CARD}>
              <h2 className={H2}>3. What the coded values mean</h2>
              <p className="mb-3 text-[13px] text-eq-neutral">
                Each value is mapped once, for every row that carries it. {preview.unmappedValues > 0 && (
                  <b className="text-eq-danger">{preview.unmappedValues} still need an answer.</b>
                )}
              </p>
              <div className="grid gap-4 md:grid-cols-2">
                {Object.entries(preview.values).filter(([, list]) => list.length > 0).map(([concept, list]) => (
                  <div key={concept}>
                    <h3 className="mb-1 font-heading text-[12.5px] font-bold text-eq-navy">{CONCEPT_TITLES[concept] ?? concept}</h3>
                    <table className="w-full text-[13px]">
                      <tbody>
                        {list.map((v) => (
                          <tr key={v.raw} className="border-b border-eq-border last:border-0">
                            <td className="py-1 pr-2">“{v.raw}” <span className="text-eq-helper">× {v.rows}</span></td>
                            <td className="py-1">
                              <select
                                className={['eq-input-sm w-full', v.target === null ? 'border-eq-danger' : ''].join(' ')}
                                value={valueMap[concept]?.[v.raw] ?? v.target ?? '__choose'}
                                onChange={(e) => setValue(concept, v.raw, e.target.value)}
                              >
                                {v.target === null && valueMap[concept]?.[v.raw] === undefined && <option value="__choose" disabled>Choose…</option>}
                                <option value="">{concept === 'GROUP_CODE' ? 'No group' : 'Leave blank'}</option>
                                {(preview.valueTargets[concept] ?? []).map(([k, label]) => (
                                  <option key={k} value={k}>{label}</option>
                                ))}
                              </select>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className={CARD}>
            <h2 className={H2}>Options</h2>
            <label className="mb-2 flex items-start gap-2 text-[13.5px]">
              <input type="checkbox" className="mt-1 h-4 w-4" checked={suggest} onChange={(e) => setSuggest(e.target.checked)} />
              <span>
                Suggest an entity type from the numbers where the file gives none — an ID number means an individual,
                a CIPC number’s last two digits name the company type, a trust number means a trust. Every suggestion is
                shown against its row below and recorded on the client.
              </span>
            </label>
            {preview.emailDomains.length > 0 && (
              <div className="mb-2 text-[13.5px]">
                <p className="mb-1">E-mail addresses at these domains are left out — tick the practice’s own:</p>
                <div className="flex flex-wrap gap-3">
                  {preview.emailDomains.map((d) => (
                    <label key={d.domain} className="inline-flex items-center gap-1.5">
                      <input
                        type="checkbox"
                        className="h-4 w-4"
                        checked={(dropDomains ?? []).includes(d.domain)}
                        onChange={(e) => setDropDomains(e.target.checked ? [...(dropDomains ?? []), d.domain] : (dropDomains ?? []).filter((x) => x !== d.domain))}
                      />
                      @{d.domain} <span className="text-eq-helper">({d.rows} rows)</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            {preview.services !== null && (
              <label className="flex items-center gap-2 text-[13.5px]">
                Imported obligations start on
                <input type="date" className="eq-input-sm" value={from ?? preview.obligationsFrom} onChange={(e) => setFrom(e.target.value)} />
                <span className="text-[12px] text-eq-helper">Periods are created from that year to next year. Earlier ones already filed can be cleared in bulk from the due list.</span>
              </label>
            )}
          </section>

          {preview.services !== null && (
            <Services
              summary={preview.services}
              serviceMap={serviceMap}
              setTarget={(key, target) => setServiceMap({ ...serviceMap, [key]: target })}
            />
          )}

          {preview.issues.length > 0 && (
            <section className={CARD}>
              <h2 className={H2}>Warnings, grouped</h2>
              <ul className="text-[13px]">
                {preview.issues.map((i) => (
                  <li key={i.message} className="border-b border-eq-border py-1 last:border-0">
                    <b className="tabular-nums">{i.rows.length}</b> × <span className="text-eq-warning">{i.message}</span>
                    <span className="ml-1 text-[11.5px] text-eq-helper">(rows {i.rows.slice(0, 8).join(', ')}{i.rows.length > 8 ? ', …' : ''})</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section className={CARD}>
            <h2 className={H2}>4. Every row</h2>
            <p className="mb-3 text-[14px]">
              {preview.counts.create} new, {preview.counts.fill} to fill in, {preview.counts.skip} already up to date,{' '}
              <span className={preview.counts.error > 0 ? 'font-semibold text-eq-danger' : ''}>{preview.counts.error} cannot be imported</span>.
              Untick a row to leave it out.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-[13px]">
                <thead>
                  <tr className="border-b border-eq-border text-left text-eq-neutral">
                    <th className="w-[3.5rem] px-2 py-1 font-semibold">Import</th>
                    <th className="w-[3.5rem] px-2 py-1 font-semibold">Row</th>
                    <th className="px-2 py-1 font-semibold">Client</th>
                    <th className="w-[8rem] px-2 py-1 font-semibold">What happens</th>
                    <th className="px-2 py-1 font-semibold">Work</th>
                    <th className="px-2 py-1 font-semibold">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r) => {
                    const [label, cls] = ACTION_WORDS[r.action]
                    const writable = r.action === 'create' || r.action === 'fill' || (r.action === 'skip' && r.work.length > 0)
                    return (
                      <tr key={r.row} className="border-b border-eq-border align-top last:border-0">
                        <td className="px-2 py-1.5">
                          <input type="checkbox" aria-label={`Import row ${r.row}`} checked={writable && !excluded.has(r.row)} disabled={!writable} onChange={() => toggle(r.row)} className="h-4 w-4" />
                        </td>
                        <td className="px-2 py-1.5 tabular-nums text-eq-neutral">{r.row}</td>
                        <td className="px-2 py-1.5">
                          {r.shortName || <span className="text-eq-helper">(no name)</span>}
                          {r.existing !== null && (
                            <div className="text-[12px] text-eq-helper">
                              already client <Link to={`/clients/${r.existing.id}`} className="underline">{r.existing.clientNumber}</Link>
                            </div>
                          )}
                        </td>
                        <td className="px-2 py-1.5">
                          <span className={`rounded-eq-pill px-2 py-0.5 font-heading text-[11px] font-bold uppercase tracking-[.06em] ${cls}`}>{label}</span>
                        </td>
                        <td className="px-2 py-1.5 text-[12px] text-eq-navy">{r.work.join(', ')}</td>
                        <td className="px-2 py-1.5 text-[12px]">
                          {r.problems.map((p) => <div key={p} className="text-eq-danger">{p}</div>)}
                          {r.fills.length > 0 && <div className="text-eq-neutral">adds {r.fills.map((f) => FIELD_WORDS[f] ?? f).join(', ')}</div>}
                          {r.notes.map((n) => <div key={n} className="text-eq-neutral">{n}</div>)}
                          {r.warnings.map((w) => <div key={w} className="text-eq-warning">{w}</div>)}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </AppShell>
  )
}

function Services({
  summary,
  serviceMap,
  setTarget,
}: {
  summary: NonNullable<ImportPreview['services']>
  serviceMap: Record<string, string>
  setTarget: (key: string, target: string) => void
}) {
  const l = summary.linked
  return (
    <section className={CARD}>
      <h2 className={H2}>3b. Services → obligations</h2>
      {l !== null && (
        <p className="mb-2 text-[13.5px]">
          Services list: {l.servicesRows} rows — {l.byId} matched to a client by ID, {l.byName} by exact name
          {l.unlinkedRows.length > 0 && (
            <b className="text-eq-danger">, {l.unlinkedRows.length} matched no client (rows {l.unlinkedRows.slice(0, 10).join(', ')}{l.unlinkedRows.length > 10 ? ', …' : ''})</b>
          )}
          {l.ambiguousNames > 0 && <span className="text-eq-warning"> — {l.ambiguousNames} names appear more than once and were not matched</span>}.
          {l.idsAdopted > 0 && ` ${l.idsAdopted} client${l.idsAdopted === 1 ? '' : 's'} take their old-system ID from the services list, so a later import recognises them.`}
        </p>
      )}
      <table className="mb-3 w-full text-[13px]">
        <thead>
          <tr className="border-b border-eq-border text-left text-eq-neutral">
            <th className="px-2 py-1 font-semibold">Service in the file</th>
            <th className="w-[5rem] px-2 py-1 font-semibold">Clients</th>
            <th className="px-2 py-1 font-semibold">Becomes</th>
          </tr>
        </thead>
        <tbody>
          {summary.services.map((s) => (
            <tr key={s.key} className={['border-b border-eq-border last:border-0', s.rows === 0 ? 'text-eq-helper' : ''].join(' ')}>
              <td className="px-2 py-1">{s.label}{s.key.startsWith('tag:') && <span className="text-eq-helper"> (tag)</span>}</td>
              <td className="px-2 py-1 tabular-nums">{s.rows}</td>
              <td className="px-2 py-1">
                <select
                  className={['eq-input-sm w-full', s.target === null && s.rows > 0 ? 'border-eq-danger' : ''].join(' ')}
                  value={serviceMap[s.key] ?? s.target ?? '__choose'}
                  onChange={(e) => setTarget(s.key, e.target.value)}
                >
                  {s.target === null && serviceMap[s.key] === undefined && <option value="__choose" disabled>Choose…</option>}
                  {summary.targets
                    .filter(([k]) => k !== 'initials' || s.key.startsWith('tag:'))
                    .map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {summary.unmappedServices > 0 && (
        <p className="mb-2 text-[13px] font-semibold text-eq-danger">{summary.unmappedServices} service(s) still need an answer — “Not imported” is one.</p>
      )}
      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <h3 className="mb-1 font-heading text-[12.5px] font-bold text-eq-navy">Will be created</h3>
          <ul className="text-[13px]">
            {summary.planned.map((p) => (
              <li key={`${p.kind}${p.code}`}>
                <b className="tabular-nums">{p.rows}</b> × {p.kind === 'job' ? 'job ' : ''}{p.code}
                {!p.hasDueDates && <span className="text-eq-warning"> — no cited deadline rule yet: recorded, without due dates</span>}
              </li>
            ))}
          </ul>
          {summary.incomeTaxWithoutEntity > 0 && (
            <p className="mt-1 text-[12.5px] text-eq-warning">
              {summary.incomeTaxWithoutEntity} income tax service(s) not imported: the client’s entity type is not known.
            </p>
          )}
          {summary.notImported.length > 0 && (
            <p className="mt-1 text-[12.5px] text-eq-neutral">
              Not imported (no type exists): {summary.notImported.map((n) => `${n.key} × ${n.rows}`).join(', ')}.
            </p>
          )}
        </div>
        <div>
          <h3 className="mb-1 font-heading text-[12.5px] font-bold text-eq-navy">Who does the work</h3>
          <ul className="text-[13px]">
            {summary.initials.map((i) => (
              <li key={i.initials}>
                <b>{i.initials}</b> <span className="text-eq-helper">({i.rows} clients)</span> —{' '}
                {i.heldBy ?? <span className="text-eq-warning">nobody holds these initials yet; record them at <Link to="/users" className="underline">User access</Link> and the work resolves to them</span>}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  )
}

function Done({ result }: { result: ImportResult }) {
  if (result.alreadyImported) {
    return (
      <section role="status" className="mb-4 rounded-eq-md border border-eq-success bg-eq-success-bg px-5 py-4 text-[14px]">
        <p className="m-0 font-semibold">This import was already done. Nothing was added a second time.</p>
      </section>
    )
  }
  const p = result.periods
  return (
    <section role="status" className="mb-4 rounded-eq-md border border-eq-success bg-eq-success-bg px-5 py-4 text-[14px]">
      <p className="m-0 font-semibold text-eq-success">
        Imported: {result.created.length} new client{result.created.length === 1 ? '' : 's'}
        {result.filled.length > 0 && `, ${result.filled.length} filled in`}
        {result.groupsCreated > 0 && `, ${result.groupsCreated} group${result.groupsCreated === 1 ? '' : 's'}`}
        {result.obligationsCreated > 0 && `, ${result.obligationsCreated} obligations`}
        {result.jobsCreated > 0 && `, ${result.jobsCreated} jobs`}.
      </p>
      {p !== null && (
        <div className="mt-2 text-[13px] text-eq-neutral">
          {p.error !== null ? (
            <p className="text-eq-danger">
              The clients are in, but creating their periods failed: {p.error}. Use “Refresh periods” on the due list to finish.
            </p>
          ) : (
            <>
              <p>
                Periods for {p.years.join(', ')}: {p.created} created, {p.dueDatesSet} dated.
                {p.refused.length > 0 && ' Not dated yet, and why:'}
              </p>
              <ul className="ml-4 list-disc">
                {p.refused.slice(0, 12).map((x) => (
                  <li key={`${x.obligationTypeCode}${x.reason}`}>{x.count} × {x.obligationTypeCode}: {x.reason}</li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
      <p className="mt-2">
        <Link to="/clients" className="font-semibold text-eq-navy underline">Go to the client list</Link>
        {' · '}
        <Link to="/" className="font-semibold text-eq-navy underline">Due and overdue</Link>
      </p>
    </section>
  )
}
